import { onDestroy } from 'svelte';

/**
 * Keyboard shortcuts.
 *
 * A component registers its shortcuts as a scope, and the open scopes form a stack. A key
 * press is offered to the innermost scope first and then outwards, until a scope handles it
 * or a modal scope stops it. Dialogs are modal: while one is open, keys it doesn't bind stay
 * away from the map and the sidebar behind it.
 *
 * A scope is identified by the object created for it rather than by a name, and it is
 * released when its component is destroyed. That way a scope going away can never take the
 * shortcuts of another one with it, which matters here since map popups are destroyed on a
 * timer after they close, typically well after the next popup has opened.
 */

/**
 * The open scopes, innermost last.
 */
const scopes = [];

/**
 * Input types that hold no text. The rest of them do, an input without a type attribute
 * included, since the browser reports those as text.
 */
const NON_TEXT_INPUT_TYPES = ['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'];

/**
 * Elements the browser activates with Enter or space on its own. A focused button does what
 * it says rather than what the surrounding dialog has bound the key to.
 */
const ACTIVATABLE_TAGS = ['BUTTON', 'A'];

/**
 * Registers keyboard shortcuts for the calling component, for as long as it lives.
 *
 * Must be called while the component is initializing, i.e. from the top level of its
 * <script> block, since the scope is released with onDestroy. Place the call below the
 * handlers it binds, so that they are defined when the bindings are built.
 *
 * @param {Object} bindings The handlers by key, e.g. { Enter: approve, Escape: cancel }.
 *                          Keys are matched against KeyboardEvent.key, lowercased for
 *                          single characters, so bind 'a' rather than 'A'. The object is
 *                          read when a key is pressed rather than when the scope opens, so
 *                          a component can put a binding in and take it out again as its
 *                          state changes. A key that isn't bound at that moment carries on
 *                          to the scope below, which is the only way to hand a key back:
 *                          a handler that decides to do nothing has already stopped it.
 * @param {Object} [options] `modal` decides whether the scope swallows the keys it doesn't
 *                           bind rather than letting the scope below handle them. Dialogs
 *                           are modal, the map and the sidebar underneath them are not.
 * @returns {Function} Releases the scope. Only needed to end it before the component does.
 */
export function keyScope(bindings, options = {}) {
    const scope = {
        bindings: bindings,
        modal: !!options.modal,
    };

    const release = () => {
        const index = scopes.indexOf(scope);
        if (index > -1) {
            scopes.splice(index, 1);
        }
    };

    // Registered before the scope is opened, so that calling this from outside a component,
    // where onDestroy throws, can't leave a scope behind that nothing will ever release.
    onDestroy(release);
    scopes.push(scope);

    return release;
}

/**
 * Ends the scopes of every open dialog.
 *
 * Dialog components outlive their dialogs: a map popup is destroyed on a timer once it has
 * closed, so that its content doesn't disappear before the fade-out is over. Left to
 * onDestroy, its shortcuts would go on swallowing keys for half a second after it is gone,
 * which is exactly when the next key is pressed. This ends them along with the dialog.
 *
 * Releasing all of them at once is enough, since only one dialog is open at a time: the map
 * closes the previous popup before opening the next one, so this runs before the scope of
 * the one being opened is registered.
 */
export function releaseModalScopes() {
    for (let i = scopes.length - 1; i >= 0; i--) {
        if (scopes[i].modal) {
            scopes.splice(i, 1);
        }
    }
}

/**
 * Handles a key press by offering it to the open scopes, innermost first.
 *
 * Hooked up once, in the capture phase, so that the shortcuts of the app are handled before
 * Leaflet gets to the event. Leaflet listens on the document while the map has the focus,
 * and stops the events it handles, Escape among them.
 *
 * @param {KeyboardEvent} event The key press to handle.
 */
export function handleKeyDown(event) {
    // A held key repeats, and a shortcut should run once per press. Composing means the key
    // is part of a character being entered with an input method.
    if (event.repeat || event.isComposing) {
        return;
    }

    // Leave the shortcuts of the browser and the system alone. event.key is the plain
    // character for those as well, so without this cmd-A would reject an edit.
    if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
    }

    const key = normalizeKey(event.key);

    if (!key || !isShortcutAvailable(key, event.target)) {
        return;
    }

    for (let i = scopes.length - 1; i >= 0; i--) {
        const scope = scopes[i];
        const handler = scope.bindings[key];

        if (handler) {
            // Nothing else should act on a key that turned out to be a shortcut, neither the
            // browser nor Leaflet.
            event.preventDefault();
            event.stopPropagation();

            handler(event);
            return;
        }

        if (scope.modal) {
            return;
        }
    }
}

/**
 * Normalizes the key of an event into the form shortcuts are bound in.
 *
 * @param {String} key The key of the event.
 * @returns {String} The key to look up, or null if the event carries no usable key.
 */
function normalizeKey(key) {
    if (!key) {
        return null;
    }

    // Single characters are matched regardless of shift and caps lock, so that shortcuts
    // don't quietly stop working with caps lock on. Named keys such as Enter are left alone.
    return key.length === 1 ? key.toLowerCase() : key;
}

/**
 * Decides whether a key press should be treated as a shortcut at all, or left to the element
 * it was aimed at.
 *
 * @param {String} key The normalized key.
 * @param {Element} element The element the event was aimed at.
 * @returns {Boolean} Whether the key can be handled as a shortcut.
 */
function isShortcutAvailable(key, element) {
    if (!element || !element.tagName) {
        return true;
    }

    // The browser activates a focused button or link with these on its own
    if ((key === 'Enter' || key === ' ') && ACTIVATABLE_TAGS.includes(element.tagName)) {
        return false;
    }

    // Escape concerns the dialog rather than whatever has the focus inside it
    if (key === 'Escape') {
        return true;
    }

    // Text entry takes every key, so that writing a description can't approve anything
    if (isTextEntry(element)) {
        return false;
    }

    // Dropdowns and checkboxes take single characters for themselves, one for type-ahead and
    // the other for space. Named keys such as Enter mean nothing to them, so a dialog can
    // still be approved with the focus on one of its fields.
    if (element.tagName === 'SELECT' || element.tagName === 'INPUT') {
        return key.length > 1;
    }

    return true;
}

function isTextEntry(element) {
    if (element.isContentEditable || element.tagName === 'TEXTAREA') {
        return true;
    }

    return element.tagName === 'INPUT' && !NON_TEXT_INPUT_TYPES.includes(element.type);
}
